import ApplicationController from "./application_controller"
import Logger from '../lib/logger'
import { showToast } from '../utils/toast'

export default class extends ApplicationController {
  static targets = [
    "tabNav", "tabButton", "tabPane", "tabContent", "progressSteps",
    "commercialStatus", "responsesStatus", "analysisStatus", "p2pStatus",
    "draftButton", "validateButton", "saveButton", "saveStatus"
  ]

  static values = {
    quoteId: Number
  }

  connect() {
    super.connect()
    Logger.debug('QuoteTabs controller connected', { quoteId: this.quoteIdValue })
    this._currentTab = "commercial"
    this._tabStates = {
      commercial: { visited: true, completed: false },
      responses: { visited: false, completed: false },
      analysis: { visited: false, completed: false },
      p2p: { visited: false, completed: false }
    }

    this._handleTabShown = this.onTabShown.bind(this)
    this.tabButtonTargets.forEach(button => {
      button.addEventListener('shown.bs.tab', this._handleTabShown)
    })

    this.updateProgressSteps()
  }

  disconnect() {
    super.disconnect()
    this.tabButtonTargets.forEach(button => {
      button.removeEventListener('shown.bs.tab', this._handleTabShown)
    })
  }

  switchTab(event) {
    const tabName = event.currentTarget.dataset.tab
    Logger.debug('Switching to tab', { tabName })

    if (this._currentTab) {
      this._tabStates[this._currentTab].visited = true
    }

    this._currentTab = tabName
    this._tabStates[tabName].visited = true

    this.updateProgressSteps()
    this.updateTabStates()
    this.loadTabContent(tabName)
  }

  onTabShown(event) {
    const tabName = event.target.dataset.tab
    Logger.debug('Tab shown', { tabName })
    this._currentTab = tabName
    this.updateProgressSteps()
    this.initializeTabContent(tabName)
  }

  loadTabContent(tabName) {
    switch (tabName) {
      case 'analysis':
        this.loadAnalysisData()
        break
      case 'p2p':
        this.loadP2PData()
        break
    }
  }

  initializeTabContent(tabName) {
    const tabPane = document.querySelector(`#${tabName}-tab`)
    if (!tabPane) return

    const controllers = tabPane.querySelectorAll('[data-controller]')
    controllers.forEach(element => {
      if (element.stimulus) {
        element.stimulus.connect()
      }
    })
  }

  updateProgressSteps() {
    if (!this.hasProgressStepsTarget) return

    const steps = this.progressStepsTarget.querySelectorAll('.step')
    steps.forEach(step => {
      const stepName = step.dataset.step
      step.classList.remove('active', 'completed', 'visited')

      if (stepName === this._currentTab) {
        step.classList.add('active')
      } else if (this._tabStates[stepName]?.completed) {
        step.classList.add('completed')
      } else if (this._tabStates[stepName]?.visited) {
        step.classList.add('visited')
      }
    })
  }

  updateTabStates() {
    if (this.hasCommercialStatusTarget) {
      const commercialComplete = this.checkCommercialCompletion()
      this._tabStates.commercial.completed = commercialComplete
      this.updateStatusIcon(this.commercialStatusTarget, commercialComplete)
    }

    if (this.hasResponsesStatusTarget) {
      const responsesComplete = this.checkResponsesCompletion()
      this._tabStates.responses.completed = responsesComplete
      this.updateStatusIcon(this.responsesStatusTarget, responsesComplete)
    }

    if (this.hasAnalysisStatusTarget) {
      this.updateStatusIcon(this.analysisStatusTarget, this._tabStates.analysis.visited)
    }

    if (this.hasP2pStatusTarget) {
      const p2pComplete = this.checkP2PCompletion()
      this._tabStates.p2p.completed = p2pComplete
      this.updateStatusIcon(this.p2pStatusTarget, p2pComplete)
    }

    this.updateProgressSteps()
  }

  updateStatusIcon(target, isComplete) {
    if (isComplete) {
      target.innerHTML = '<i class="fas fa-check-circle text-success"></i>'
    } else if (this._tabStates[this._currentTab]?.visited) {
      target.innerHTML = '<i class="fas fa-exclamation-circle text-warning"></i>'
    } else {
      target.innerHTML = '<i class="fas fa-circle text-secondary"></i>'
    }
  }

  checkCommercialCompletion() {
    const nrcField = document.querySelector('input[name*="[nrc]"]')
    const mrcField = document.querySelector('input[name*="[mrc]"]')
    const currencyField = document.querySelector('input[name*="[currency]"]')

    const hasNRC = nrcField && parseFloat(nrcField.value) > 0
    const hasMRC = mrcField && parseFloat(mrcField.value) > 0
    const hasCurrency = currencyField && currencyField.value.trim() !== ""

    return (hasNRC || hasMRC) && hasCurrency
  }

  checkResponsesCompletion() {
    const responseCards = document.querySelectorAll('.supplier-quote-card')
    let hasCompleteResponse = false

    responseCards.forEach(card => {
      const providerField = card.querySelector('input[name*="[providername]"]')
      const nrcField = card.querySelector('input[name*="[nrc]"]')
      const mrcField = card.querySelector('input[name*="[mrc]"]')

      const hasProvider = providerField && providerField.value.trim() !== ""
      const hasNRC = nrcField && parseFloat(nrcField.value) > 0
      const hasMRC = mrcField && parseFloat(mrcField.value) > 0

      if (hasProvider && (hasNRC || hasMRC)) {
        hasCompleteResponse = true
      }
    })

    return hasCompleteResponse
  }

  checkP2PCompletion() {
    const p2pResponses = document.querySelectorAll('#p2p-tab .supplier-quote-card')
    return p2pResponses.length > 0
  }

  async saveDraft() {
    Logger.debug('Saving draft')
    this.updateSaveStatus("Saving draft...", "saving")

    try {
      const form = document.querySelector(`#edit_quote_${this.quoteIdValue}`) ||
                   document.querySelector(`#responses_form_${this.quoteIdValue}`)

      if (!form) {
        throw new Error("Form not found")
      }

      const formData = new FormData(form)

      const response = await this.safeFetch(`/quotes/${this.quoteIdValue}`, {
        method: 'PATCH',
        body: formData,
        headers: {
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
          'Accept': 'application/json'
        }
      })

      if (response.ok) {
        this.updateSaveStatus("Draft saved", "success")
        showToast("Draft saved successfully", "success")
      } else {
        throw new Error("Save failed")
      }
    } catch (error) {
      Logger.error('Draft save failed', error)
      this.updateSaveStatus("Save failed", "error")
      showToast("Failed to save draft", "error")
    }
  }

  async validateAll() {
    Logger.debug('Validating all tabs')

    const validationResults = {
      commercial: this.checkCommercialCompletion(),
      responses: this.checkResponsesCompletion(),
      analysis: true,
      p2p: this.checkP2PCompletion()
    }

    const allValid = Object.values(validationResults).every(result => result)

    if (allValid) {
      showToast("All tabs validated successfully!", "success")
      this.updateTabStates()
    } else {
      const invalidTabs = Object.keys(validationResults)
        .filter(tab => !validationResults[tab])
        .join(", ")

      showToast(`Validation failed for: ${invalidTabs}`, "error")
    }

    return allValid
  }

  refreshAnalysis() {
    Logger.debug('Refreshing analysis data')

    const crmFrame = document.querySelector('turbo-frame[id="crm"]')
    if (crmFrame && crmFrame.src) {
      crmFrame.reload()
    }

    const mapFrame = document.querySelector('turbo-frame[id="quotemap"]')
    if (mapFrame && mapFrame.src) {
      mapFrame.reload()
    }

    showToast("Analysis data refreshed", "info")
  }

  loadAnalysisData() {
    Logger.debug('Loading analysis data')
  }

  loadP2PData() {
    Logger.debug('Loading P2P data')
  }

  updateSaveStatus(message, type) {
    if (!this.hasSaveStatusTarget) return

    const iconMap = {
      saving: "fa-spinner fa-spin",
      success: "fa-check-circle",
      error: "fa-exclamation-circle",
      info: "fa-info-circle"
    }

    const classMap = {
      success: "success",
      error: "danger",
      saving: "warning",
      info: "muted"
    }

    const icon = iconMap[type] || iconMap.info
    const cssClass = classMap[type] || classMap.info

    this.saveStatusTarget.innerHTML = `
      <i class="fas ${icon} me-1"></i>
      ${message}
    `

    this.saveStatusTarget.className = `text-${cssClass}`
  }
}
